import { useEffect, useMemo } from "react";

const GLYPHS = ["🎉", "✨", "🟡", "💸", "🎊", "⭐"];

/** One-shot emoji burst. Renders nothing under reduced motion. */
export default function Confetti({
  count,
  onDone,
}: {
  count: number;
  onDone: () => void;
}) {
  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const pieces = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => {
        // Deterministic spread: spiral outward so pieces never overlap.
        const angle = (Math.PI * 2 * i) / count + (i % 3) * 0.2;
        const dist = 90 + ((i * 37) % 120);
        return {
          glyph: GLYPHS[i % GLYPHS.length],
          dx: `${Math.cos(angle) * dist}px`,
          dy: `${Math.sin(angle) * dist - 40}px`,
          rot: `${((i * 71) % 360) - 180}deg`,
        };
      }),
    [count],
  );

  useEffect(() => {
    const t = setTimeout(onDone, reduce ? 0 : 1100);
    return () => clearTimeout(t);
  }, [onDone, reduce]);

  if (reduce) return null;
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-1/3 z-50 flex justify-center"
    >
      <div className="relative h-0 w-0">
        {pieces.map((p, i) => (
          <span
            key={i}
            className="bill-confetti absolute text-xl"
            style={
              {
                "--dx": p.dx,
                "--dy": p.dy,
                "--rot": p.rot,
              } as React.CSSProperties
            }
          >
            {p.glyph}
          </span>
        ))}
      </div>
    </div>
  );
}
